import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { DiscordSDK } from '@discord/embedded-app-sdk';

interface DiscordContextType {
  discordSdk: DiscordSDK;
  isReady: boolean;
}

interface DiscordProviderProps {
  children: React.ReactNode;
}

const DiscordContext = createContext<DiscordContextType | null>(null);

export function useDiscord() {
  const context = useContext(DiscordContext);
  if (!context) {
    throw new Error('useDiscord must be used within a DiscordProvider');
  }
  return context;
}

export default function DiscordProvider({ children }: DiscordProviderProps) {
  const [discordSdk, setDiscordSdk] = useState<DiscordSDK | null>(null);
  const [isReady, setIsReady] = useState(false);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const initDiscord = async () => {
      try {
        const clientId = new URLSearchParams(window.location.search).get('client_id') 
          || import.meta.env.VITE_DISCORD_CLIENT_ID
          || '1471221053622849710';

        const sdk = new DiscordSDK(clientId);
        await sdk.ready();
        setDiscordSdk(sdk);
        setIsReady(true);
        console.log('Discord SDK initialized successfully');
      } catch (error) {
        console.error('Failed to initialize Discord SDK:', error);
        setIsReady(true);
      }
    };

    initDiscord();
  }, []);

  const contextValue: DiscordContextType = {
    discordSdk: discordSdk!,
    isReady: isReady && !!discordSdk,
  };

  return (
    <DiscordContext.Provider value={contextValue}>
      {children}
    </DiscordContext.Provider>
  );
}
